/**
 * Shared field schema for the intake form, mirroring the business-profile
 * template shipped with the assessment program. The backend's parallel
 * section/field key list (`backend/app/domain/schema.py`) is sync-tested.
 *
 * Every FieldDef should have a non-empty `hint` — it surfaces as a tooltip in the UI.
 */

import { ACE_COUNTRIES } from '../utils/countries'
import type { ChoiceOption, SectionDef } from './types'

// The schema-neutral types live in ./types (shared with the company-profile /
// WAFR / FTR schemas); re-exported for existing importers.
export type { ChoiceOption, FieldDef, FieldType, Phase, SectionDef } from './types'

/** Options whose stored value is also the label. */
const plain = (...values: string[]): ChoiceOption[] => values.map((v) => ({ value: v, label: v }))

export const INDUSTRY_OPTIONS: ChoiceOption[] = plain(
  'Automotive', 'CPG', 'Education', 'Energy & Utilities', 'Financial Services',
  'Games', 'Healthcare & Life Sciences', 'Manufacturing', 'Restaurants', 'Retail',
  'Telecommunications', 'Other',
)

// Values are AWS's own segment codes (the Fund Request's activity name embeds
// them); labels spell each code out so nobody has to decode "SUP" mid-intake.
const AWS_SEGMENT_OPTIONS: ChoiceOption[] = [
  { value: 'Enterprise', label: 'Enterprise (ENT) — ≤ $30K cap' },
  { value: 'ISV', label: 'ISV — Independent Software Vendor — ≤ $30K cap' },
  { value: 'STRAT', label: 'STRAT — Strategic account — ≤ $30K cap' },
  { value: 'SMB', label: 'SMB — Small & Midsize Business — ≤ $15K cap' },
  { value: 'SUP', label: 'SUP — Startup — ≤ $15K cap' },
  { value: 'MMT', label: 'MMT — Mid-Market — ≤ $15K cap' },
  { value: 'Mixed', label: 'Mixed — ≤ $15K cap' },
]

/** The standard country list ACE uses (US first, then alphabetical) — the
 *  HQ-country dropdown stores the two-letter ISO code. */
const COUNTRY_OPTIONS: ChoiceOption[] = ACE_COUNTRIES.map(([code, name]) => ({
  value: code,
  label: name,
}))

const COMPANY_SIZE_OPTIONS = plain('SMB', 'Mid-Market', 'Enterprise')

/** 0–4 maturity scale used by the per-dimension readiness scores (captured once
 *  here in intake — they drive the Phase 2 readiness report). */
const MATURITY_OPTIONS: ChoiceOption[] = [
  { value: '0', label: '0 — Not Started' },
  { value: '1', label: '1 — Ad Hoc' },
  { value: '2', label: '2 — Developing' },
  { value: '3', label: '3 — Established' },
  { value: '4', label: '4 — Optimized' },
]

const GEO_OPTIONS = plain('Single region', 'Multi-region', 'Global')

const AWS_RELATIONSHIP_OPTIONS = plain('No usage', 'Exploratory', 'Established', 'Strategic')

const URGENCY_OPTIONS: ChoiceOption[] = [
  { value: 'High', label: 'High (event-driven)' },
  { value: 'Medium', label: 'Medium (strategic)' },
  { value: 'Low', label: 'Low (exploratory)' },
]

const OPPORTUNITY_THEME_OPTIONS: ChoiceOption[] = [
  ...plain('Cost Reduction', 'Revenue Growth', 'Customer Experience', 'Operational Efficiency'),
  { value: 'Compliance/Risk', label: 'Compliance / Risk' },
]

const AI_MATURITY_OPTIONS: ChoiceOption[] = [
  { value: 'Greenfield', label: 'Greenfield — no AI yet' },
  { value: 'Experimenting', label: 'Experimenting — pilots, no production' },
  { value: 'Scaling', label: 'Scaling — moving to production' },
  { value: 'Optimizing', label: 'Optimizing — multiple production workloads' },
]

const AWS_PATTERN_OPTIONS = plain(
  'Serverless-first', 'Container-based', 'EC2 lift-and-shift', 'Hybrid', 'On-prem only',
)

const IAM_MATURITY_OPTIONS = plain('Least-privilege', 'Mostly defined', 'Ad hoc')

const AWS_SERVICES_OPTIONS = plain(
  'Bedrock', 'SageMaker', 'Lambda', 'ECS', 'EKS', 'EC2', 'S3', 'RDS', 'DynamoDB',
  'Redshift', 'Glue', 'Athena', 'API Gateway', 'CloudWatch', 'CloudTrail',
  'Cognito', 'IAM Identity Center', 'KMS', 'Secrets Manager', 'EventBridge',
  'SQS', 'SNS', 'Step Functions', 'CloudFront', 'Route 53', 'WAF', 'Shield',
)

const COMMON_SAAS_OPTIONS = plain(
  'Salesforce', 'ServiceNow', 'Workday', 'Jira', 'Confluence', 'SharePoint',
  'Slack', 'Microsoft Teams', 'Zendesk', 'HubSpot', 'NetSuite', 'SAP',
  'Oracle', 'Microsoft 365', 'Google Workspace',
)

const DATA_CATALOG_OPTIONS: ChoiceOption[] = [
  { value: 'Glue Data Catalog', label: 'AWS Glue Data Catalog' },
  ...plain('Collibra', 'Alation', 'None', 'Other'),
]

const DATA_QUALITY_OPTIONS: ChoiceOption[] = [
  { value: 'Good', label: 'Good — production-ready' },
  { value: 'Mixed', label: 'Mixed — some cleanup needed' },
  { value: 'Needs work', label: 'Needs work — significant gaps' },
]

const COMPLIANCE_OPTIONS = plain(
  'HIPAA', 'SOC2', 'GDPR', 'CCPA', 'FedRAMP', 'PCI-DSS', 'HITRUST', 'ISO 27001',
  'NIST', 'GLBA', 'FERPA',
)

const AI_POLICY_OPTIONS: ChoiceOption[] = [
  { value: 'Yes', label: 'Yes — formal policy in place' },
  { value: 'Partial', label: 'Partial — guidelines exist, not formalized' },
  { value: 'No', label: 'No' },
]

const SPONSOR_COMMITMENT_OPTIONS = plain('Strongly supportive', 'Supportive with conditions', 'Skeptical')

const BUDGET_STATUS_OPTIONS = plain('Confirmed', 'Under discussion', 'Not yet')

/** Contact types for the company contact roster. Billing/procurement contacts
 *  live here too — "who do we invoice" is company data, not engagement data. */
export const CONTACT_TYPE_OPTIONS: ChoiceOption[] = plain(
  'Executive', 'Technical', 'Product', 'Billing', 'Procurement',
  'Security / Compliance', 'Legal / Signatory', 'Other',
)

export const SECTIONS: readonly [SectionDef, ...SectionDef[]] = [
  {
    key: 'company_profile',
    number: 0,
    phase: 'company',
    title: 'Company Profile',
    navTitle: 'Company',
    description:
      'The basics of who this company is — name, industry, size, what they do, and who to talk to. Captured once on the standalone Company profile page and shared by every assessment and module; web research drafts most of it when the company has a URL.',
    fields: [
      {
        key: 'legal_name',
        label: 'Legal name',
        hint:
          'Full legal entity name as it appears on invoices and tax documents (e.g., "Best Buy Co., Inc.", not just "Best Buy"). This is the name AWS will see on the ACE Opportunity.',
        type: { kind: 'text', placeholder: 'Best Buy Co., Inc.' },
        priority: 'required',
      },
      {
        key: 'aliases',
        label: 'Also known as / abbreviations',
        hint:
          'Other names this customer goes by — DBAs, abbreviations, tickers (e.g. "LPS" for "Let\'s Play Sports"). Used to find an existing ACE Opportunity even when it was filed under a different name. One per line.',
        type: { kind: 'list', placeholder: 'One alias / abbreviation per line' },
      },
      {
        key: 'business_description',
        label: 'Business description',
        hint:
          'What the company does in plain language — products or services, who their customers are, and how they make money. Web research proposes a draft from the company website; edit it into the two-to-four sentences you would say out loud to a colleague.',
        help:
          '## Example\n\n"Atlas Logistics is a mid-market third-party logistics (3PL) provider running regional warehousing and last-mile delivery for e-commerce brands across the western US. Revenue comes from per-pallet storage fees and delivery contracts. Customers are ~200 DTC brands; peak season triples volume."',
        type: { kind: 'textarea', rows: 4 },
        priority: 'recommended',
      },
      {
        key: 'industry',
        label: 'Industry',
        hint:
          "Map to the closest AWS industry vertical. The program maintains an Industry AI Use Case Catalog organized by these verticals, so the choice drives which catalog suggests relevant use cases for this engagement. If nothing fits, pick \"Other.\"",
        type: { kind: 'radio', options: INDUSTRY_OPTIONS },
        priority: 'required',
      },
      {
        key: 'sub_industry',
        label: 'Sub-industry / domain',
        hint:
          'More specific descriptor inside the chosen industry. Example: industry=Retail, sub-industry="Consumer electronics retail" or "Specialty grocery". Helps narrow the relevant use-case catalog for this customer.',
        help:
          "## Examples\n\n- Retail → \"Consumer electronics retail\" / \"Specialty grocery\" / \"Apparel — fast fashion\"\n- Financial Services → \"Mid-market commercial banking\" / \"Personal auto insurance\" / \"Wealth management — RIA\"\n- Healthcare → \"Multi-site primary care\" / \"Specialty pharmacy\" / \"Regional health plan\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'company_size',
        label: 'Company size',
        hint:
          'Rough sizing of the customer: SMB (small/medium, <500 employees), Mid-Market (500–5,000), Enterprise (5,000+ or major brand). Used both for AWS segment validation and to size the proposed POC scope.',
        type: { kind: 'radio', options: COMPANY_SIZE_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'hq_city',
        label: 'HQ city',
        hint:
          'City of the customer headquarters. Used in the executive readout, timezone planning, and the ACE opportunity address.',
        type: { kind: 'text', placeholder: 'Richfield' },
        half: true,
      },
      {
        key: 'hq_state',
        label: 'HQ state / region',
        hint:
          'State, province, or region — free-form, not just US states ("WA", "Bavaria", "Ontario").',
        type: { kind: 'text', placeholder: 'MN' },
        half: true,
      },
      {
        key: 'hq_postal_code',
        label: 'HQ postal code',
        hint:
          'ZIP or postal code of the headquarters. The ACE opportunity address uses it.',
        type: { kind: 'text', placeholder: '55423' },
        half: true,
      },
      {
        key: 'hq_country',
        label: 'HQ country',
        hint:
          'Country of the headquarters — the standard ISO list ACE uses. Required on every ACE opportunity address.',
        type: { kind: 'select', options: COUNTRY_OPTIONS },
        half: true,
      },
      {
        key: 'contacts',
        label: 'Contacts',
        hint:
          'Everyone we talk to at the company, with their role: executive sponsor, technical leads, billing/procurement, legal or the sign-off signatory. One row per person — email is what most workflows need; phone is optional.',
        type: { kind: 'contacts-table' },
        priority: 'recommended',
      },
    ],
  },
  {
    key: 'funding_eligibility',
    number: 1,
    phase: 'intake',
    title: 'Funding Eligibility',
    description:
      'Confirm the engagement qualifies for the AWS AI Assessment Funding 2026 program. Check every box the engagement satisfies; all 7 must be checked to qualify. Any box left unchecked is a disqualifier.',
    fields: [
      {
        key: 'checks',
        label: 'Eligibility checks',
        priority: 'required',
        hint:
          'The 7 hard rules from the AWS program. Tick the box on each rule the engagement satisfies — all 7 must be checked to qualify. Click the ⓘ on each row for a plain-English definition.',
        type: {
          kind: 'eligibility-grid',
          rows: [
            {
              value: 'not_government',
              label: 'Not a government / public-sector customer',
              hint:
                'The customer cannot be substantially owned, funded, managed, or controlled by any government — federal, state, local, or foreign.',
              help:
                "## What this means\n\nThis program is for **commercial** companies only. Public-sector entities (federal agencies, state/local governments, foreign governments, public universities, government-owned utilities) are not eligible.\n\n## Why it exists\n\nAWS runs **separate** programs for the public sector with their own funding pools and compliance requirements — for example, AWS GovCloud and dedicated public-sector partner tracks. Those programs handle the procurement rules (FAR, FedRAMP, ITAR, etc.) that don't apply to commercial work.\n\n## How to evaluate\n\n- **Private company or non-profit?** → Pass.\n- **Wholly government-owned utility / agency?** → Fail.\n- **Mostly private but partially government-funded?** Check the ownership and funding structure. If government control is *substantial* — board seats, majority funding, day-to-day operational control — treat as Fail and route to a public-sector partner.\n\nWhen in doubt, ask the AWS Account Manager — they'll know which partner program fits.",
            },
            {
              value: 'not_greenfield',
              label: 'Not greenfield / startup',
              hint:
                '**The customer** must be an established, operating business — not a brand-new startup or a company still in its incubation phase.',
              help:
                "## What this means\n\nThe **customer** (the company we'll be doing the AI assessment for) must already be a running business with real operations, revenue, and customers. The funding pays for AI modernization of an **existing** business — not for helping someone launch a brand-new company.\n\n## Common confusion\n\nThis check is about the *customer*, **not** about the project. It's totally fine if the AI use cases we're proposing are brand-new (\"we've never done agents before\"). What matters is that the customer themselves is already operating.\n\n## How to evaluate\n\n- **Established mid-market or enterprise company?** → Pass.\n- **Seed/early-stage startup still finding product-market fit?** → Fail.\n- **Recently-funded scale-up with paying customers?** → Likely Pass — but document revenue scale in the customer profile so AWS sees the customer is an established business.\n\nA rough rule of thumb: if they have a recognizable name, paying customers, and have been operating for years, they're not greenfield.",
            },
            {
              value: 'not_migration_only',
              label: 'Not a migration-only engagement',
              hint:
                'If the engagement is purely about migrating existing workloads to AWS without an AI/agent component, it belongs in MAP, not AI Assessment Funding.',
              help:
                "## What this means\n\nThis funding is for **AI work**, not lift-and-shift cloud migrations. If the customer's goal is \"move our existing data center to AWS,\" that's a different AWS program called **MAP** (Migration Acceleration Program) and has its own funding.\n\n## Why it exists\n\nAWS funds different things from different buckets. AI Assessment Funding specifically pays for the discovery work that goes *before* a real AI/agent POC. If there's no AI component, this money isn't the right fit.\n\n## How to evaluate\n\n- **\"We want to deploy AI agents to do X\"** → Pass.\n- **\"We want to migrate our SAP install to AWS\"** → Fail, route to MAP.\n- **\"We want to migrate *and* also explore AI\"** → Pass only if the AI scope is real and substantial. The migration can happen alongside, but the assessment work has to be focused on the AI piece. Make sure the use cases the consultant captures are real AI workloads, not just \"AWS-native versions of what we already do.\"",
            },
            {
              value: 'not_active_poc',
              label: 'Not already in active POC or production for these use cases',
              hint:
                'The customer cannot already be running a paid POC or production deployment for any of the use cases this assessment will cover.',
              help:
                "## What this means\n\nThe funding is for the **discovery work that happens before a POC**. Once a real POC is paid for and underway, that use case has moved past the discovery phase and AI Assessment Funding is no longer the right bucket.\n\n## Examples\n\n- Customer is *thinking about* a support-ticket assistant, but hasn't built anything yet → **Pass**, this is exactly the use case.\n- Customer already has a Bedrock + LangChain POC running for ticket triage → **Fail** for that specific use case. They can still get assessment funding for *different* AI use cases that haven't started yet.\n- Customer has a production fraud-detection ML model already deployed → **Pass** as long as the use cases this assessment covers are different from that fraud model. Existing production ML on unrelated workloads doesn't disqualify.\n\n## How to evaluate\n\nList the use cases the customer wants this assessment to cover. For each one, ask: \"Has the customer paid anyone (us, another partner, internally) to build a POC or production deployment for *this specific use case*?\" If yes, that use case is out of scope — drop it or replace it.",
            },
            {
              value: 'invoicing_entity_different',
              label: 'Invoicing entity ≠ customer name',
              hint:
                "Self-assessment is disqualifying. The partner that invoices AWS for the funding cannot also be the customer being assessed.",
              help:
                "## What this means\n\nAllCode (the partner that's claiming the funding) cannot also be the customer of the engagement. The customer must be a separate, third-party company.\n\n## Why it exists\n\nThe program is structured so that AWS funds partners to do **assessment work for their customers**. If a partner could assess themselves, they could essentially claim funding for internal R&D — which isn't what the program is for.\n\n## How to evaluate\n\n- **Customer is one of AllCode's external customers** → Pass.\n- **Customer is AllCode itself, or an AllCode subsidiary** → Fail.\n- **Customer is a former AllCode employee's new company, or some other arms-length-but-related entity** → Probably Pass, but document the relationship in the customer profile so AWS can confirm.\n\nIf this row would be \"Fail,\" the engagement isn't eligible — full stop. There's no workaround.",
            },
            {
              value: 'first_assessment',
              label: 'First AI Assessment for this customer',
              hint:
                'A customer can only receive one AI Assessment Funding per program. If this customer has already had an assessment under this program, the engagement is not eligible.',
              help:
                "## What this means\n\nAWS funds one assessment per customer per program cycle. If this customer already received AI Assessment Funding (from AllCode or any other AWS partner), they can't receive a second one.\n\n## How to verify\n\nThe AWS Account Manager can check the customer's record in their internal funding system. **Always** ask them to confirm before kicking off — relying on the customer's memory is risky because they may not know about an assessment a different partner ran.\n\n## What if they already had one?\n\nThe customer isn't disqualified from working with us — they just can't receive *funding* for this specific program. We can still propose paid POC work or scope a different program (Sprint 2 when it opens, MAP for migration, etc.).",
            },
            {
              value: 'fits_agentcore_or_quick',
              label: 'Plausibly fits AgentCore and/or Quick Suite',
              hint:
                "Sprint 1's theme is \"Modernization to Agents.\" The assessment must evaluate fit for Amazon Bedrock AgentCore and/or Amazon Quick Suite.",
              help:
                "## What this means\n\nSprint 1 of the program has a specific theme: **\"Modernization to Agents.\"** The deliverables we produce must evaluate at least one of these two AWS platforms:\n\n- **Amazon Bedrock AgentCore** — AWS's agentic AI runtime. Think: multi-turn AI agents that use tools, hold context, and take actions. Use it when the customer needs reasoning + tool calls + memory.\n- **Amazon Quick Suite** — AWS's BI + agents + automation suite. Think: \"give business users an AI that can answer questions and run workflows on their data.\" Use it when the customer needs analytics-style queries + drafted workflows on top of their existing data.\n\nA proposal can use one, the other, or both.\n\n## How to evaluate\n\n- Customer wants an internal copilot, customer-facing chatbot, or autonomous workflow → **AgentCore fit**, Pass.\n- Customer wants natural-language analytics, BI agents, semantic search over their docs → **Quick Suite fit**, Pass.\n- Customer's only AI ask is \"train a custom fraud-detection model\" → traditional ML, **doesn't fit Sprint 1**. Fail (or shift focus to an agentic use case alongside the ML one).\n\nIf nothing in the customer's needs plausibly maps to either platform, this engagement doesn't fit Sprint 1 and shouldn't go through the program.",
            },
          ],
        },
      },
    ],
  },
  {
    key: 'customer_identity',
    number: 2,
    phase: 'intake',
    title: 'AWS Relationship & Footprint',
    navTitle: 'AWS Relationship',
    description:
      "The customer's standing with AWS: segment (sets the funding cap), relationship maturity, regions, spend, and in-scope accounts. Company basics (name, industry, size) live on the Company profile.",
    fields: [
      {
        key: 'aws_segment',
        label: 'AWS customer segment',
        hint:
          "The segment AWS's own account team has this customer filed under — a code in AWS's CRM, not something to work out from company size. It sets the funding cap ($30K for Enterprise / ISV / STRAT, $15K for the rest). Get it from the AWS Account Manager.",
        help:
          "## What this is\n\nAWS files every customer under one **segment** in its own CRM. It is AWS's sales classification, **not** a description of the company, so you can't reliably work it out from headcount or revenue. A 40-person software company is often **ISV**, not SMB; a large company can sit in **SMB** if that's where AWS put it.\n\nIt matters because it sets the **funding cap**, and the Fund Request is checked against what AWS has on file. A wrong segment can delay or reject funding.\n\n## How to get it\n\n1. **Ask the AWS Account Manager**: \"Which segment is this customer in?\" They can read it straight from their CRM. This is the only reliable source.\n2. **The customer usually won't know.** It's an AWS-internal label, so don't ask them to guess.\n3. **Not sure yet?** Pick your best guess and keep going (don't block the intake on it), but add a line under **Additional Context** that the segment still needs AM confirmation. It must be confirmed before the Fund Request is filed.\n\n## The segments\n\n**$30K cap**\n- **Enterprise (ENT)**: large organizations AWS manages through its enterprise team.\n- **ISV (Independent Software Vendor)**: companies whose *product* is software they sell to others (SaaS, packaged software). A company that just *uses* software heavily is not an ISV.\n- **STRAT (Strategic)**: a short list of AWS's most important accounts. Rare; the AM will tell you if it applies.\n\n**$15K cap**\n- **SMB (Small & Midsize Business)**\n- **SUP (Startup)**\n- **MMT (Mid-Market)**\n- **Mixed**: the account spans segments.\n\n## Common mix-ups\n\n- **ISV vs SMB.** Ask: *do they sell software as their product?* If yes, check with the AM whether they're filed as ISV. It doubles the cap.\n- **Enterprise vs STRAT.** Default to Enterprise. Only pick STRAT if the AM confirms it.\n- **Picking by size.** Don't. Two companies of the same size can be in different segments.",
        type: { kind: 'radio', options: AWS_SEGMENT_OPTIONS },
        priority: 'required',
      },
      {
        key: 'geographic_scope',
        label: 'Geographic scope',
        hint:
          "Where the customer operates today. Affects data-residency requirements (GDPR for EU, sovereign-cloud needs, etc.) and which AWS regions are in scope for the architecture recommendation.",
        type: { kind: 'radio', options: GEO_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'regions',
        label: 'Regions',
        hint:
          'Specific AWS regions the customer uses or is considering. Influences Bedrock model availability (not every model is in every region) and latency planning.',
        help:
          "## Examples\n\n- \"us-east-1 (primary), us-west-2 (DR)\"\n- \"eu-west-1, eu-central-1 — strict EU-only for GDPR\"\n- \"us-east-1 today, exploring ap-southeast-2 for APAC expansion in 2026\"",
        type: { kind: 'textarea', rows: 2, placeholder: 'us-east-1, us-west-2, eu-west-1' },
      },
      {
        key: 'aws_relationship',
        label: 'AWS relationship maturity',
        hint:
          'How established the customer already is on AWS today.',
        help:
          "## What this is\n\nA snapshot of how deep the customer is on AWS today. It tells the proposal how much foundational work has to happen versus how much we can build on top.\n\n## The four levels\n\n- **No usage** — Customer has no AWS account at all. POC will include account setup, IAM, billing, networking from scratch. Slower to deliver.\n- **Exploratory** — Customer has an AWS account but it's mostly trial workloads, no real production. Some foundations exist (account, basic IAM) but many gaps. POC will need to fill them.\n- **Established** — Customer runs real production workloads on AWS. Has a named Account Manager. Has IAM, billing, networking, basic governance figured out. The POC can focus on the AI work, not the foundations.\n- **Strategic** — Customer is one of AWS's biggest accounts. Multi-year commitments, dedicated TAM, sometimes a full Solutions Architect team on retainer. Everything is in place; the POC slots into a sophisticated existing platform.\n\n## How to pick\n\nThe simplest signal: **do they have a named AWS Account Manager who knows them by name?**\n\n- No → \"No usage\" or \"Exploratory\"\n- Yes → \"Established\" or \"Strategic\"\n\nIf you're not sure between Established vs Strategic, ask the AM directly — Strategic is a small list and they'll know.",
        type: { kind: 'radio', options: AWS_RELATIONSHIP_OPTIONS },
        priority: 'required',
      },
      {
        key: 'annual_aws_spend',
        label: 'Approximate annual AWS spend (if known)',
        hint:
          'Sizes the POC investment and the AWS architecture recommendation realistically. Leave blank if unknown — the AWS Account Manager can fill this in.',
        help:
          "## Examples\n\n- \"$5M/year — on an EDP (Enterprise Discount Program). Mostly EC2, S3, Redshift.\"\n- \"~$800K/year, no discount program yet. Heavy Lambda + DynamoDB.\"\n- \"Unknown — Account Manager will fill in. Customer's billing team is the source of truth.\"",
        type: { kind: 'textarea', rows: 2, placeholder: '$5M' },
        priority: 'recommended',
      },
      {
        key: 'aws_account_ids',
        label: 'AWS account IDs in scope',
        hint:
          "The specific 12-digit AWS account IDs that the proposed solutions will deploy into. If the customer uses AWS Organizations, list the OU + member accounts. Leave blank if not known yet — the AgentCore SA can help identify these.",
        help:
          "## Examples\n\n- \"123456789012 (sandbox), 234567890123 (prod). Both members of the `ai-workloads` OU.\"\n- \"Organizations setup: management 111111111111. OUs: `dev` (3 member accounts), `prod` (5 member accounts). POC will land in the dev OU first.\"\n- \"Unknown — AgentCore SA will work with the customer cloud team to designate.\"",
        type: { kind: 'textarea', rows: 2 },
        priority: 'recommended',
      },
    ],
  },
  {
    key: 'business_context',
    number: 3,
    phase: 'intake',
    title: 'Business Context',
    description:
      'The business problem and its urgency: priorities, pain points, and where money, time, or customers are being lost. This is what the AI use cases have to address.',
    fields: [
      {
        key: 'top_priorities',
        label: 'Top strategic priorities (12–18 months)',
        hint:
          "The 3–5 things the executive team is being measured on this year (and next). Each AI proposal should map back to one of these. Quote the customer's own language where possible — the closer to verbatim, the more credible the executive readout.",
        help:
          "## Examples (one priority per line, verbatim from the customer where possible)\n\n- \"Grow same-store sales 8% in FY26. Reduce contact-center cost 15%. Cut return processing time 50%.\"\n- \"Launch in 3 new EU markets by Q3. Hit SOC 2 Type II by year-end. Reduce engineering on-call burden.\"\n- \"Hit $5M ARR by Q4. Ship the consolidated platform release. Reduce churn from 12% to 8%.\"",
        type: { kind: 'list', placeholder: 'One priority per line' },
        priority: 'recommended',
      },
      {
        key: 'pain_points',
        label: 'Biggest operational pain points',
        hint:
          'Where the business is bleeding — cost overruns, customer churn, slow throughput, compliance risk. The proposed AI solutions should target the top 2-3 of these. Be specific (e.g., "8-minute average handle time on 50K monthly support tickets" beats "support is slow").',
        help:
          "## Examples (one pain point per line, with the number that quantifies it)\n\n- \"8-minute average handle time on 50K monthly support tickets. 22% repeat-contact rate. Agent attrition 35% annual.\"\n- \"Field engineers spend 40% of shifts on diagnosis paperwork instead of repair work. ~$3M/year in lost wrench-time.\"\n- \"5-day average underwriter cycle on consumer loans. ~30% drop-off between application and decision because applicants get frustrated and bail.\"",
        type: { kind: 'list' },
        priority: 'recommended',
      },
      {
        key: 'lost_money_time_customers',
        label: 'Where they lose money / time / customers today',
        hint:
          'Quantify the cost of the status quo. Examples: "$2M/year in callback time on supplier-onboarding paperwork", "10% cart abandonment from slow checkout". This is the baseline against which ROI is measured in Phase 4.',
        help:
          "## What to capture\n\nThe dollar-cost of doing nothing — what the status quo costs the business per year, broken down by mechanism. Phase 4 ROI is computed as the savings AI captures against this baseline.\n\n## Examples\n\n- \"$2M/year in callback time on supplier-onboarding paperwork — 4 onboarding specialists × $90K loaded × 70% of their time spent on follow-up.\"\n- \"~10% cart abandonment from slow checkout, ~$8M/year in lost GMV. Adds another ~$1M in support tickets from confused customers who got stuck.\"\n- \"~$5M/year in avoidable claim leakage — adjusters under-applying contract terms because the contracts are too complex to read in time.\"",
        type: { kind: 'textarea', rows: 3 },
        priority: 'recommended',
      },
      {
        key: 'competitive_ai_pressure',
        label: 'Competitive AI pressure',
        hint:
          'What named competitors are doing with AI that worries the customer. Examples: "Walmart launched AI inventory forecasting in 2025", "Costco rolled out AI customer chat". Helps frame the urgency of the proposed solutions.',
        help:
          "## Examples\n\n- \"Walmart launched AI inventory forecasting in 2025 — early reports say 18% reduction in stockouts. Our merchandising team is being asked weekly when we're going to match it.\"\n- \"A direct competitor (DocuSign) shipped an AI contract-review agent in Q1. Sales is losing 1-2 deals/month where the customer says 'we want what they have.'\"\n- \"No specific competitor pressure yet, but board has flagged 'AI laggard' as a brand risk for FY26. Pressure is internal, not competitive.\"",
        type: { kind: 'textarea', rows: 3 },
      },
      {
        key: 'cx_falling_short',
        label: 'Customer-facing experiences falling short',
        hint:
          'Specific customer journeys with measurably poor outcomes — long wait times, low NPS, high return rates, abandoned applications. Strong fit for agentic AI proposals (contact-center copilots, intelligent assistants).',
        help:
          "## Examples\n\n- \"Mortgage-application abandonment — 38% of applicants drop before document upload. NPS on the abandoned segment is -42.\"\n- \"Support-ticket first-response time averages 14 hours. NPS for customers with multi-touch tickets is 28 points below the rest of the base.\"\n- \"Returns experience — 22% of returns require a callback because the self-service portal can't handle anything beyond exact-SKU swaps. CSAT on returns is 2.8/5.\"",
        type: { kind: 'textarea', rows: 3 },
      },
      {
        key: 'ex_inefficiencies',
        label: 'Employee experiences that are frustrating or inefficient',
        hint:
          "What internal users (associates, engineers, ops staff) complain about. Often the cheapest wins are here — AI coding-assistant rollouts, FAQ chatbots over HR docs, agentic Q&A over Confluence. Maps directly to non-agentic proposals like \"AI coding assistant for developers.\"",
        help:
          "## Examples\n\n- \"Engineers spend an estimated 4 hrs/week each searching Confluence + Slack for setup runbooks. A new-hire onboarding ramp is 8 weeks instead of the targeted 4.\"\n- \"Support agents juggle 6 tools per ticket — Salesforce, ServiceNow, the internal product DB, the returns portal, Slack for SME escalation, and the wiki. ~30% of handle time is tool-switching.\"\n- \"Field technicians have to call dispatch for parts lookup because the mobile app doesn't expose stock-on-truck. Adds 6-8 minutes per call.\"",
        type: { kind: 'textarea', rows: 3 },
      },
      {
        key: 'first_workflow_to_automate',
        label: "The one workflow they'd automate first, and why",
        hint:
          "If they could only pick one thing, what is it? This is often the lead proposal. The \"why\" matters as much as the workflow itself — it surfaces the customer's decision criteria.",
        help:
          "## What to capture\n\nForce the customer to pick **one**. That answer becomes the lead proposal in Phase 1. Capture the \"why\" — it tells you what trade-offs they value (speed, cost, risk).\n\n## Examples\n\n- \"L1 support-ticket triage — highest volume, clearest savings, lowest regulatory risk. Sponsor would approve it tomorrow if we said yes.\"\n- \"Underwriter pre-screen for consumer loans — biggest cycle-time pain. Risk team is comfortable because it's only a recommendation, not a decision.\"\n- \"Internal Confluence/Slack search — fast win that builds team confidence with agents before tackling anything customer-facing.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'opportunity_theme',
        label: 'Primary opportunity theme',
        hint:
          'The single dominant value driver for the engagement. Use this to keep the executive readout focused — every proposal should connect back to this theme.',
        type: { kind: 'radio', options: OPPORTUNITY_THEME_OPTIONS },
      },
      {
        key: 'urgency_level',
        label: 'Urgency level',
        hint:
          'High — there\'s a specific event driving the timeline (board mandate, fiscal year, competitive threat). Medium — it\'s a strategic priority but no fixed deadline. Low — exploratory, "we want to understand AI options."',
        type: { kind: 'radio', options: URGENCY_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'urgency_driver',
        label: 'Urgency driver (if high)',
        hint:
          'What event is forcing the timeline. Examples: "FY26 board commitment to ship AI in 6 months", "Competitor X just launched and we\'re losing deals", "Compliance deadline 2026-09-30."',
        help:
          "## Examples\n\n- \"FY26 board commitment to ship 2 production AI use cases by end of Q3.\"\n- \"Competitor (DocuSign) launched AI contract-review in Q1, sales is losing deals — CRO wants a counter ready by Q2.\"\n- \"Compliance deadline 2026-09-30 — new state regulation requires automated decisioning audit trails, and our manual process can't keep up.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'success_12mo',
        label: 'What does success look like in 12 months?',
        hint:
          "The customer's own picture of a win one year out — shipped use cases, metrics moved, capability built. Anchors the executive readout's recommendation and the 1-year roadmap.",
        help:
          '## Why ask this\n\nThe assessment ends with a recommendation and a 1-year roadmap. Both land much harder when they echo the customer\'s OWN definition of success instead of a generic one.\n\n## Examples\n\n- "Two agentic use cases in production (contact center + claims triage), AHT down 40%, an internal AI platform team of 3 stood up."\n- "Every underwriter using the pre-screen copilot daily; decision turnaround from 5 days to 1; zero compliance findings."\n- "Stop losing deals to competitors on AI features — ship the customer-facing assistant and measure NPS +10."',
        type: { kind: 'textarea', rows: 2 },
        priority: 'recommended',
      },
    ],
  },
  {
    key: 'ai_landscape',
    number: 4,
    phase: 'intake',
    title: 'Current AI & Automation Landscape',
    navTitle: 'AI & Automation',
    description:
      'What AI and automation the customer already runs: production ML, RPA, chatbots, GenAI pilots, and any competing platforms. Gauges their maturity and where agents can add value.',
    fields: [
      {
        key: 'production_ml_workloads',
        label: 'AI/ML workloads in production',
        hint:
          'AI/ML systems already serving customers or running the business — recommendation engines, fraud detection, demand forecasting, etc. Establishes the customer\'s AI sophistication and where the new proposals fit alongside.',
        help:
          "## Examples\n\n- \"Demand forecasting via SageMaker (weekly retrain, owns ~$40M/yr in inventory decisions). Fraud detection via a custom XGBoost model on Lambda. Recommendation engine on the e-commerce site, vendor-provided.\"\n- \"Two production models — propensity-to-churn (SageMaker, monthly batch) and customer-segment classifier (in-house, real-time on EKS). No GenAI in prod yet.\"\n- \"None — the team has read about ML but nothing is in production. This assessment is the first real foray.\"",
        type: { kind: 'textarea', rows: 3 },
        priority: 'recommended',
      },
      {
        key: 'rpa_tools',
        label: 'RPA / automation tools in use',
        hint:
          "Robotic Process Automation tools — UiPath, Automation Anywhere, Power Automate, Blue Prism, etc. RPA workloads are prime candidates for \"RPA → agentic AI\" modernization proposals.",
        help:
          "## Examples\n\n- \"UiPath — ~40 bots in production. Mostly finance + AR/AP back-office. Maintained by a 6-person CoE.\"\n- \"Power Automate (M365) — light usage in HR and IT helpdesk. ~10 flows, mostly approvals + notifications.\"\n- \"None today. Customer has considered RPA but never committed; sees agentic AI as a leapfrog option.\"",
        type: { kind: 'textarea', rows: 2, placeholder: 'UiPath, Automation Anywhere…' },
      },
      {
        key: 'existing_chatbots',
        label: 'Existing chatbots / virtual assistants (and satisfaction)',
        hint:
          "Any rule-based or older AI chatbots in production (Dialogflow, Lex, custom). Note the customer's satisfaction — \"mixed CSAT\" chatbots are great modernization candidates for agentic AI.",
        help:
          "## Examples\n\n- \"Amazon Lex bot on the website, deflects ~12% of inbound. CSAT 2.4/5 — customers complain it can't handle multi-step questions. Strong candidate to replace with an agentic version.\"\n- \"Salesforce Einstein Bots on the support page. Decent for FAQ-style questions, poor for anything order-specific. Sponsor wants to keep Einstein but augment with an agentic copilot for human agents.\"\n- \"None. Considered Dialogflow 2 years ago and shelved it.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'genai_pilots',
        label: 'GenAI experiments / pilots (models, platforms)',
        hint:
          'Any active GenAI/LLM pilots — including platform (Bedrock, OpenAI, Azure OpenAI, Vertex), foundation models in use, and outcomes. Scopes the maturity of the proposed roadmap.',
        help:
          "## Examples\n\n- \"Bedrock pilot: Claude Sonnet for internal Q&A over Confluence (~150 weekly users). Outcome: positive, no production budget yet. Also dabbled with Titan embeddings for RAG.\"\n- \"Azure OpenAI (GPT-4o) wired into a Copilot Studio prototype for the sales team. Stalled — security review blocked rollout pending data-loss controls.\"\n- \"None yet — leadership is wary of vendor lock-in and wants this assessment to drive the first commitment.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'competing_platforms',
        label: 'Workloads on competing AI/agent platforms (migration candidates)',
        hint:
          'AI workloads running on non-AWS platforms — OpenAI Assistants, Azure AI Studio, Google Vertex AI Agents, etc. These become "Migration" type proposals for moving to AgentCore/Quick.',
        help:
          "## Examples\n\n- \"Internal HR assistant on OpenAI Assistants API — works but cost is climbing and procurement is questioning the renewal. Strong migration candidate.\"\n- \"Sales-research agent on Azure AI Studio + GPT-4o. Tied to existing M365 tenant. Migration would need to preserve the Outlook/SharePoint integrations.\"\n- \"None. All current GenAI work is on Bedrock or unbuilt.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'automation_candidates',
        label: 'Manual repetitive workflows flagged as automation candidates',
        hint:
          "Internally-known \"someday we'll automate this\" workflows. These are low-hanging proposal candidates because the customer already has the business case figured out.",
        help:
          "## Examples (one per line)\n\n- \"Vendor invoice intake + GL coding — currently 2 FTEs, ~6K invoices/month.\"\n- \"New-hire IT provisioning — manual ticket-driven across 5 systems, ~3 hrs per new hire.\"\n- \"Sales-call note summarization + CRM update — reps say it takes 15 minutes per call and they skip it.\"",
        type: { kind: 'list' },
      },
      {
        key: 'ai_maturity',
        label: 'AI maturity',
        hint:
          "Self-assessment of where the customer is on their AI journey.",
        help:
          "## What this is\n\nA rough position on a 4-step ladder. Drives how ambitious the proposed roadmap should be.\n\n## The four levels\n\n- **Greenfield** — No AI in production. No pilots. The team is reading about agents and LLMs but hasn't shipped anything. Recommendations should be conservative — one well-scoped POC, not a portfolio.\n- **Experimenting** — One or two pilots are running. Maybe a Bedrock proof-of-concept, maybe a chatbot. Nothing in production yet. The team has some intuition for what works. Pick a POC the team can finish, with a clear path to production after.\n- **Scaling** — At least one AI workload is in production, serving real users. The team is now learning the *operational* side — monitoring, drift, on-call. Recommendations can be more ambitious — multiple proposals, one POC + one production expansion.\n- **Optimizing** — Multiple AI workloads in production. The team is squeezing cost, latency, accuracy. The customer can probably handle complex multi-agent architectures and Quick Suite + AgentCore combined deployments.\n\n## How to pick\n\nUse the **highest-bar test that passes:**\n\n1. \"Do they have any AI workload serving real users today?\" No → Greenfield or Experimenting.\n2. \"Have they shipped a pilot to internal users at minimum?\" No → Greenfield. Yes but not production → Experimenting.\n3. \"Production with one workload?\" → Scaling. Multiple? → Optimizing.\n\nIf the customer says \"we're really excited about AI!\" but has nothing built, that's still **Greenfield**. Excitement isn't maturity.",
        type: { kind: 'radio', options: AI_MATURITY_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'prior_ai_outcomes',
        label: 'Prior AI/ML outcomes (positive / mixed / negative)',
        hint:
          'How past AI projects landed. Positive history means the customer trusts AI investment; mixed/negative means the readout needs to emphasize risk mitigation and explicit success criteria.',
        help:
          "## Examples\n\n- \"Positive. Demand-forecasting model in production for 3 years, attributable to ~$10M/yr in inventory cost reduction. Leadership trusts AI investment.\"\n- \"Mixed. Recommendation engine shipped in 2022, never quite hit targets. A 2023 GenAI chatbot pilot was killed after privacy concerns. Sponsor is supportive but cautious — emphasize guardrails + success criteria up front.\"\n- \"Negative. A $1M GenAI 'innovation' spend in 2024 produced no production system. CFO is now skeptical of any AI investment without a clear ROI ladder.\"",
        type: { kind: 'textarea', rows: 2 },
      },
    ],
  },
  {
    key: 'tech_stack',
    number: 5,
    phase: 'intake',
    title: 'Tech Stack & AWS Footprint',
    navTitle: 'Tech Stack & AWS',
    description:
      "The customer's AWS footprint and engineering setup: architecture pattern, active services, IAM maturity, Bedrock posture, and CI/CD. Drives AgentCore / Quick Suite feasibility.",
    fields: [
      {
        key: 'aws_infrastructure_summary',
        label: 'AWS infrastructure summary',
        hint:
          "A narrative description of the customer's AWS estate — accounts, regions, compute mix, databases and data stores, networking, identity, security services, and AI usage. The AWS infrastructure scan drafts this automatically; edit it into the story a reader needs.",
        help:
          "## What this is\n\nA prose summary of the customer's AWS infrastructure — the paragraph you'd say out loud to bring a colleague up to speed, not a checklist. The AWS scan generates a draft from the live account inventory (Cost Explorer + the actual resources: EC2, ECS/EKS, Lambda, RDS engines and versions, DynamoDB, Redshift, S3, VPCs, IAM, GuardDuty/Security Hub, Bedrock usage). Accept the finding, then edit.\n\n## Example\n\n\"Two AWS accounts under one organization, active in us-east-1 and us-west-2, spending roughly $87K/year (top services: EC2, Bedrock, S3, RDS). Compute is mixed: 3 EC2 instances, 14 Lambda functions, and one App Runner service. Data lives in RDS Postgres 16 (20 GB, encrypted, single-AZ), 20 S3 buckets (~4 GB), and no DynamoDB. Networking uses 2 VPCs (one custom) with an S3 gateway endpoint and one NAT gateway. 12 IAM users and no Identity Center. GuardDuty and Security Hub (FSBP) are on. Bedrock is in production use — ~8.4K Claude invocations in the last 30 days with one guardrail configured.\"",
        type: { kind: 'textarea', rows: 5 },
        priority: 'recommended',
      },
      {
        key: 'aws_pattern',
        label: 'AWS architecture pattern',
        hint:
          "The customer's dominant deployment pattern on AWS. Drives integration choices for the proposals — e.g., a serverless-first shop will want AgentCore + Lambda; a container-based shop fits AgentCore + ECS/EKS.",
        type: { kind: 'radio', options: AWS_PATTERN_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'active_aws_services',
        label: 'Active AWS services',
        hint:
          'Tick everything the customer actively uses today. The architecture recommendation will integrate with these rather than introducing parallel stacks. The "Other" box accepts comma-separated extras.',
        type: { kind: 'multiselect', options: AWS_SERVICES_OPTIONS, allowOther: true },
        priority: 'recommended',
      },
      {
        key: 'bedrock_models',
        label: 'Bedrock models enabled',
        hint:
          'Which foundation models the customer has activated in Amazon Bedrock.',
        help:
          "## What Bedrock is\n\n**Amazon Bedrock** is AWS's managed service for using foundation models (LLMs) through an API. Instead of running models yourself, you call Bedrock and it runs models from Anthropic, Meta, Amazon, etc. on your behalf inside your AWS account.\n\n## What \"enabled\" means\n\nEvery Bedrock model has to be **explicitly turned on** per AWS account and region. You request access, AWS reviews (usually instant), then the model can be called. A customer might have an AWS account but **zero Bedrock models** enabled — in that case, the very first POC step is enabling them.\n\n## What to capture\n\nList the specific model IDs the customer has activated. Examples:\n\n- `anthropic.claude-sonnet-4-20250514-v1:0`\n- `amazon.titan-embed-text-v2:0`\n- `meta.llama-3-1-70b-instruct-v1:0`\n\nIf the customer doesn't know, look in their AWS console under **Bedrock → Model access**, or ask their AWS Account Manager.\n\n## Why it matters for the proposal\n\nIf they have **no models enabled**, the POC plan needs an explicit \"enable models\" step at the start.\n\nIf they only have older-generation models enabled, the proposal should call out an upgrade to newer ones.",
        type: { kind: 'textarea', rows: 2, placeholder: 'anthropic.claude-sonnet-4-…, amazon.titan-embed-v2…' },
        priority: 'recommended',
      },
      {
        key: 'bedrock_quota',
        label: 'Bedrock quota constraints (known)',
        hint:
          'Quotas on Bedrock model invocations per minute, tokens per minute, etc.',
        help:
          "## What quotas are\n\nEvery Bedrock model has **rate limits** — how many requests per minute (RPM), input tokens per minute (TPM), and output tokens per minute the account is allowed to send. AWS sets a default ceiling and you can request increases.\n\n## Why it matters\n\nA POC at low volume will rarely hit defaults. **Production traffic almost always does.** If we plan an agent that handles 500 concurrent users without checking quotas, we'll hit walls and the customer will blame us.\n\n## What to capture\n\nAsk the customer or their AWS account team:\n\n1. **Have they ever requested a quota uplift?** If yes, get the current limits.\n2. **Are they at default service quotas?** That's the common case — note it.\n3. **Is the planned workload likely to exceed defaults?** Anything more than a small internal pilot probably will.\n\n## What to do about it\n\nIf the planned workload exceeds quotas:\n\n- The proposal should include an **\"RPM uplift request\"** as a pre-launch task.\n- The AWS Account Manager or AgentCore SA can submit the uplift request — it's usually fast (a few days) but can take longer for big jumps.\n\nIf you genuinely don't know, write \"Default service quotas; uplift needed for production\" and flag it in the discovery gaps.",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'iam_maturity',
        label: 'IAM maturity',
        hint:
          'How rigorous the customer is about IAM (Identity & Access Management).',
        help:
          "## What IAM is\n\n**IAM (Identity and Access Management)** is AWS's permission system. Every API call to AWS is authenticated as some IAM identity (a user, role, or assumed role) and authorized against IAM policies. The quality of a customer's IAM setup determines how safely we can plug an agent into their account.\n\n## What \"maturity\" means here\n\n- **Least-privilege** — Roles are tightly scoped to exactly what each workload needs. No `Action: \"*\"` policies. Reviewed and audited. The customer can confidently give the agent its own role with narrow permissions.\n- **Mostly defined** — Most workloads have dedicated roles, but some have overly broad permissions or stale unused ones. Workable for a POC, but the proposal should call out an IAM-tightening task.\n- **Ad hoc** — A handful of admin-equivalent roles get reused everywhere. Not safe to attach an agent to without an IAM cleanup first.\n\n## Why it matters for the proposal\n\nAgentCore agents authenticate as IAM principals. If the customer's IAM is ad-hoc, the POC budget needs to include an **IAM hardening sub-task** before any agent can take real actions. Without it, the agent ends up with effectively-unlimited permissions in the account — a real security problem.\n\n## How to evaluate\n\nAsk:\n- Do they review IAM policies regularly (e.g., quarterly access reviews)?\n- Do they use AWS Organizations / SCPs / Permissions Boundaries?\n- Do they have an SSO provider (Okta, Entra, etc.) feeding IAM?\n\nThree yeses → Least-privilege. One or two → Mostly defined. None → Ad hoc.",
        type: { kind: 'radio', options: IAM_MATURITY_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'iac_cicd',
        label: 'IaC & CI/CD',
        hint:
          "What's used for Infrastructure-as-Code (CDK, Terraform, CloudFormation, Pulumi) and CI/CD (CodePipeline, GitHub Actions, Jenkins, CircleCI, Azure DevOps). The AgentCore deployment design must fit the customer's existing automation.",
        help:
          "## Examples\n\n- \"Terraform + GitHub Actions. All AWS resources behind Atlantis with PR-based plan/apply.\"\n- \"AWS CDK (TypeScript) + CodePipeline. Monorepo with per-stage CodeBuild stacks.\"\n- \"CloudFormation templates + Jenkins. Manual approval gate before production apply.\"",
        type: { kind: 'textarea', rows: 2, placeholder: 'Terraform + GitHub Actions' },
      },
      {
        key: 'observability',
        label: 'Observability stack',
        hint:
          "How the customer monitors production today (CloudWatch, Datadog, Dynatrace, Splunk, New Relic, OpenTelemetry, etc.). AgentCore traces and metrics should integrate with this stack via OTLP export — don't impose a parallel observability stack.",
        help:
          "## Examples\n\n- \"Datadog for everything — APM, logs, dashboards, alerts. Custom OTLP collectors in EKS. No CloudWatch outside billing.\"\n- \"CloudWatch Logs + CloudWatch Metrics for AWS-native services, Splunk for application logs via Firehose. Grafana Cloud for dashboards.\"\n- \"New Relic APM + logs. CloudWatch only for AWS service metrics that New Relic doesn't pull.\"",
        type: { kind: 'textarea', rows: 2, placeholder: 'CloudWatch, Datadog…' },
      },
      {
        key: 'auth_idp',
        label: 'Authentication / IdP',
        hint:
          'Identity Provider — Amazon Cognito, Okta, Microsoft Entra ID (formerly Azure AD), Auth0, Ping, custom OIDC. This is what AgentCore Identity will federate against for user-to-agent authorization.',
        help:
          "## Examples\n\n- \"Okta for workforce. Cognito for customer-facing apps. SAML federation between the two for cross-app SSO.\"\n- \"Microsoft Entra ID (formerly Azure AD) — single tenant, all workforce + customer apps federated. Conditional Access policies enforced.\"\n- \"Custom OIDC provider on the customer's internal IAM platform. Okta for AWS console access only.\"",
        type: { kind: 'textarea', rows: 2, placeholder: 'Cognito, Okta…' },
      },
      {
        key: 'networking',
        label: 'Networking (PrivateLink, VPC endpoints, on-prem connectivity)',
        hint:
          'Network isolation requirements — PrivateLink endpoints for Bedrock, VPC-only AgentCore deployments, Direct Connect/VPN to on-prem systems. Critical for regulated industries.',
        help:
          "## Examples\n\n- \"All AWS traffic via VPC endpoints. Bedrock via Interface Endpoint with PrivateLink. Direct Connect to two on-prem data centers (10 Gbps active/active). No internet egress except via the centralized inspection VPC.\"\n- \"Hub-and-spoke VPC with Transit Gateway. AWS PrivateLink for S3, Bedrock, Secrets Manager. Site-to-site VPN to the on-prem SAP estate. NAT Gateway for limited outbound to vendor SaaS.\"\n- \"No restrictive requirements — public AWS endpoints with TLS are acceptable for the POC. Production may move to PrivateLink later.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'saas_systems',
        label: 'Major SaaS systems agents would call',
        hint:
          'External SaaS tools the agents need to reach via API (Salesforce, ServiceNow, Workday, Jira, Confluence, SharePoint, Slack, Zendesk, etc.). Each becomes an AgentCore Gateway integration or MCP server. The "Other" box accepts comma-separated extras.',
        type: { kind: 'multiselect', options: COMMON_SAAS_OPTIONS, allowOther: true },
      },
      {
        key: 'internal_systems',
        label: 'Major internal/custom systems agents would call (APIs available?)',
        hint:
          "In-house apps the agents need to integrate with. Crucially: do they have HTTP/JSON APIs? If not, integration is a bigger lift — flag in Phase 2 readiness as a gap.",
        help:
          "## Examples\n\n- \"Order Management System — internal REST API, well-documented. Inventory Service — gRPC, no REST gateway yet (will need a wrapper). Customer 360 — only SQL access via read replicas, no API.\"\n- \"Three in-house apps. (1) Pricing engine — REST API, OAuth. (2) Loan-origination platform — SOAP only, will need a translation layer. (3) Claims system — direct DB access only, no service tier.\"\n- \"Everything customer-facing already has REST APIs. Back-office is a mix — payroll has APIs, vendor management does not.\"",
        type: { kind: 'textarea', rows: 3 },
      },
      {
        key: 'knowledge_bases',
        label: 'Existing knowledge bases',
        hint:
          'Sources of unstructured knowledge the agents could draw from — Confluence, SharePoint, internal wikis, product manuals on S3, FAQ pages, customer support knowledge base. Becomes the input for Bedrock Knowledge Base or Quick Index.',
        help:
          "## Examples\n\n- \"Confluence Cloud — ~12K pages, engineering + product runbooks. Service knowledge base on Salesforce — ~3K articles, customer-facing. Product manuals as PDFs on SharePoint.\"\n- \"SharePoint Online — HR policies, IT runbooks. ServiceNow KB — ~5K articles, mostly stale. No central wiki.\"\n- \"S3 bucket of PDF contracts (~12K), no current index. Internal wiki on a self-hosted MediaWiki (~800 pages). No formal knowledge base.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'vector_store_rag',
        label: 'Existing vector store / RAG infra',
        hint:
          "If they've already built RAG: which vector DB, embedding model, chunking strategy.",
        help:
          "## What RAG is\n\n**RAG** = **Retrieval-Augmented Generation.** The pattern where, before asking the LLM a question, you:\n\n1. Convert the question into a **vector embedding** (a list of numbers that captures meaning).\n2. Search a **vector store** for chunks of text whose embeddings are nearest to that question.\n3. Hand the LLM both the question **and** those retrieved chunks as context.\n\nThis lets the LLM answer questions grounded in the customer's own documents, instead of just whatever it learned during training.\n\n## What to capture\n\nIf the customer already has a RAG setup, document:\n\n- **Vector database:** OpenSearch Serverless, Pinecone, Weaviate, pgvector (Postgres extension), Aurora PostgreSQL Vector, Qdrant, etc.\n- **Embedding model:** Titan Embeddings v2, Cohere Embed v3, OpenAI text-embedding-3, etc.\n- **Chunking strategy:** how documents are split (by paragraph, by token count, semantic chunking).\n- **What it's indexed over:** the actual data (Confluence pages? PDFs? ticket history?).\n\n## Why it matters\n\nIf they already have RAG working over the right data, the proposal can **reuse** it. If they don't, the POC will need to **build** it — that's weeks of work and a budget line item.\n\nIf you don't know, write \"None\" and flag it as a discovery gap.",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'maturity_score',
        label: 'Architecture readiness maturity (0–4)',
        hint:
          'Your overall read of the AWS/architecture foundation: account structure, Bedrock footprint, IAM, CI/CD, observability. Drives the Phase 2 readiness report.',
        type: { kind: 'radio', options: MATURITY_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'mcp_servers',
        label: 'Existing MCP servers (if any)',
        hint:
          'Model Context Protocol servers the customer has stood up to expose internal tools to agents.',
        help:
          "## What MCP is\n\n**MCP** = **Model Context Protocol.** A standard (originally by Anthropic, now widely adopted) for how an AI agent talks to **tools** — APIs, databases, internal systems.\n\nThe idea: instead of every agent framework inventing its own way to expose tools to the model, MCP defines one protocol. You write an **MCP server** that wraps your internal tool (e.g., \"look up an order in our order DB\"), and any MCP-aware agent can use it. AgentCore is MCP-aware.\n\n## In plain terms\n\nAn MCP server is the **plumbing** between an agent and a real system. Without one, the agent can only talk; with one, it can actually take action — look up a customer, file a ticket, query a database.\n\n## What to capture\n\nAsk the customer:\n\n- Have they built any MCP servers yet? (Most customers haven't — this is a 2024+ technology.)\n- If yes, which internal systems do they cover? (e.g., \"we have an MCP server that wraps our Salesforce read APIs.\")\n- If yes, who maintains them?\n\n## Why it matters\n\n- **They have MCP servers already** → big tailwind. The agent POC can plug in immediately.\n- **They don't** → the POC needs to **build** the MCP servers for whatever tools the agent needs. That's a real engineering line item — usually 2-4 servers for a typical POC, each a couple weeks of work.\n\nIf they say \"what's MCP?\", that's a clear \"None\" answer — note it as a foundational workstream the POC will create.",
        type: { kind: 'textarea', rows: 2 },
      },
    ],
  },
  {
    key: 'data_readiness',
    number: 6,
    phase: 'intake',
    title: 'Data Readiness',
    description:
      'Where the data lives and how usable it is: stores, volume, catalog, quality, freshness, and known gaps. Determines what a POC can realistically retrieve and act on.',
    fields: [
      {
        key: 'primary_locations',
        label: 'Primary data locations',
        hint:
          'Where the data the agents need actually lives: S3 buckets, RDS/Aurora databases, Redshift warehouses, SaaS exports, on-prem databases, file shares, etc. The architecture recommendation must source from these.',
        help:
          "## Examples\n\n- \"Redshift warehouse (sales, inventory, finance marts). S3 data lake for raw events (~80 TB). Salesforce as the CRM system-of-record. On-prem Oracle for ERP (replicated nightly to RDS).\"\n- \"Aurora PostgreSQL for the transactional product database. Snowflake for analytics. SharePoint for unstructured policy docs. No on-prem.\"\n- \"S3 (PDF contracts, ~12K docs), DynamoDB (customer profiles), ServiceNow (tickets — accessed via REST API).\"",
        type: { kind: 'textarea', rows: 2 },
        priority: 'recommended',
      },
      {
        key: 'volume',
        label: 'Volume of relevant data',
        hint:
          'Rough volume estimate — gigabytes? terabytes? petabytes? per data store. Drives capacity planning for Bedrock Knowledge Base, embedding pipelines, and Quick Index sizing.',
        help:
          "## Examples\n\n- \"50 TB transactional + catalog data. Knowledge base ~2 GB.\"\n- \"~500 GB structured warehouse data. ~30 GB of PDF contracts. ~5 GB Confluence content.\"\n- \"~10 PB raw event data in S3, but only ~80 GB is in scope for the agent (the curated KB).\"",
        type: { kind: 'textarea', rows: 2, placeholder: '50 TB transactional + catalog' },
      },
      {
        key: 'data_catalog',
        label: 'Data catalog',
        hint:
          'Whether the customer maintains a data catalog (AWS Glue Data Catalog, Collibra, Alation). Catalogs are critical for grounding agents in correct semantic definitions of business terms — without one, expect hallucination on data questions.',
        type: { kind: 'radio', options: DATA_CATALOG_OPTIONS },
      },
      {
        key: 'data_quality',
        label: 'Data quality maturity for AI',
        hint:
          'How clean the data is. "Good" — production-grade, type-checked, deduplicated. "Mixed" — usable but needs cleanup per use case. "Needs work" — significant quality gaps that will surface as agent errors and require remediation up front.',
        type: { kind: 'radio', options: DATA_QUALITY_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'freshness',
        label: 'Data freshness required vs. current cadence',
        hint:
          'How fresh the agent needs the data to be (real-time, hourly, daily, weekly) vs. how often the source data is updated. Mismatch surfaces a data-pipeline gap.',
        help:
          "## Examples\n\n- \"Agent needs real-time order status. Source (OMS) is real-time via REST. KB content updates weekly. Match — no pipeline gap.\"\n- \"Customer expects 'live' inventory — daily batch is the current cadence. Mismatch: POC plan needs a CDC pipeline or a wait-time disclosure to the user.\"\n- \"Daily-fresh content is fine for the knowledge base. Confluence currently re-indexes nightly. Match.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'languages',
        label: 'Languages in data',
        hint:
          'Which natural languages the data is in (English, Spanish, French, Chinese, etc.). Drives model selection — not every Bedrock model performs equally across languages. Multilingual customers need explicit testing.',
        help:
          "## Examples\n\n- \"English only.\"\n- \"English (primary, ~85% of content), Spanish (~15%, customer-facing tickets).\"\n- \"English, French, German, Mandarin Chinese, Japanese — multilingual customer base. Model evals will need a held-out set per language.\"",
        type: { kind: 'textarea', rows: 2, placeholder: 'English, Spanish, French' },
      },
      {
        key: 'pii_sensitivity',
        label: 'Sensitive data classes in scope (PII / PHI / PCI / trade secrets)',
        hint:
          'What regulated or sensitive data the candidate use cases would touch. Drives the guardrail design in Phase 3, the governance review, and the security nodes in the architecture diagrams.',
        help:
          '## Examples\n\n- "Customer PII (names, addresses, order history) in every contact-center interaction. No PHI/PCI — payments are tokenized by Stripe."\n- "PHI throughout — clinical notes, diagnoses, medications. HIPAA BAA with AWS in place; PHI must never leave us-east-1."\n- "Underwriting files contain PII + credit data (FCRA). Trade-secret pricing models must not be exposed to any model context."\n- "None — the knowledge base is public product documentation."',
        type: { kind: 'textarea', rows: 2 },
        priority: 'recommended',
      },
      {
        key: 'biases_gaps',
        label: 'Known biases or gaps',
        hint:
          'Known underrepresented groups, blind spots, or stale segments in the data. Surfaces in Phase 2 readiness and informs the responsible-AI guardrails in Phase 3.',
        help:
          "## Examples\n\n- \"Customer KB skews toward English-speaking US customers — APAC + EU customers under-represented despite being 30% of revenue. Multilingual evaluation set needed.\"\n- \"Historical hiring data over-represents one university feeder. Any agent that surfaces 'similar candidates' would amplify the bias — explicitly out of scope for the POC.\"\n- \"None known. Customer hasn't done a formal data-bias audit; flag as a Phase 2 discovery gap.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'maturity_score',
        label: 'Data readiness maturity (0–4)',
        hint:
          'Your overall read of data availability, quality, pipelines, and AI-readiness across what the use cases need. Drives the Phase 2 readiness report.',
        type: { kind: 'radio', options: MATURITY_OPTIONS },
        priority: 'recommended',
      },
    ],
  },
  {
    key: 'governance',
    number: 7,
    phase: 'intake',
    title: 'Governance, Compliance & Security',
    navTitle: 'Governance & Security',
    description:
      'Compliance, security, and AI-policy constraints: frameworks in scope, data residency, guardrails, and prohibitions. Sets the non-negotiable boundaries for any solution.',
    fields: [
      {
        key: 'compliance_frameworks',
        label: 'Applicable regulatory frameworks',
        hint:
          'Compliance regimes the customer operates under. HIPAA (healthcare), SOC2 (general), GDPR/CCPA (privacy), PCI-DSS (payments), FedRAMP (US gov), HITRUST (healthcare), etc. Each may require specific AgentCore Policy and audit-logging settings.',
        type: { kind: 'multiselect', options: COMPLIANCE_OPTIONS, allowOther: true },
        priority: 'recommended',
      },
      {
        key: 'data_residency',
        label: 'Data residency / sovereignty requirements',
        hint:
          'Constraints on which countries/regions data may live in or transit through. EU-only customers need EU-region Bedrock + AgentCore; some customers prohibit cross-border data transfer entirely.',
        help:
          "## What to capture\n\nThe rules the customer's legal / compliance team places on where data may live and which AWS services may touch it. These constraints often pre-determine which AWS regions and which Bedrock models are usable for the POC.\n\n## Examples\n\n- \"All PHI must reside in HIPAA-eligible AWS services. Specific BAA-covered services only. No cross-region without legal review.\"\n- \"EU customer data must remain in eu-west-1 or eu-central-1. No transfer to US regions, even for logging. Sub-processor list must be pre-approved by DPO.\"\n- \"No restrictions today, but the customer expects a new internal policy in Q3 — assume US-only is the safe default and revisit if it changes.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'ai_policy',
        label: 'Existing AI / Responsible AI policy',
        hint:
          "Whether the customer has a formal AI usage policy (acceptable use, model approval, model card requirements). \"Partial\" — they have guidelines but no formal review process. \"No\" — the proposed POC needs to draft one as a prerequisite.",
        type: { kind: 'radio', options: AI_POLICY_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'review_process',
        label: 'AI review / approval process for production',
        hint:
          "How a new AI workload gets cleared for production (legal review, security review, model-risk-management committee, etc.). Affects the POC → production timeline materially — bake the customer's process into the roadmap.",
        help:
          "## What to capture\n\nThe gates a new AI workload has to pass before it can serve real users in production. Capture the named bodies, what each one reviews, and rough cycle times — the POC roadmap has to bake them in.\n\n## Examples\n\n- \"Model Risk Management committee reviews every new model — meets monthly, 6-week lead time for submission. Plus security review (1-2 weeks) and legal review for any customer-facing output (2-3 weeks). All three run in parallel after the POC ends.\"\n- \"No formal AI review yet — security review only (standard SDLC). Customer expects to stand up an AI governance board in 2026; we should propose a lightweight one as part of the POC.\"\n- \"For internal-only tools, just standard change-advisory-board sign-off (weekly). For anything customer-facing, add legal + compliance — adds ~4 weeks. POC plan should keep the first launch internal-only to skip the longer path.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'guardrails',
        label: 'Required guardrails',
        hint:
          'Required runtime safety guardrails: PII redaction, content filtering, topic restrictions, hallucination detection, denial of jailbreak prompts. Bedrock Guardrails and AgentCore Policy cover most of these.',
        help:
          "## What to capture\n\nConcrete runtime rules that the agent must obey. Each one maps to a specific Bedrock Guardrails policy or an AgentCore Policy entry.\n\n## Examples\n\n- \"PII redaction on user input + on log output (mask SSN, DOB, member-ID, email). Topic restriction: no medical advice — defer to a clinician. Hallucination grounding: refuse to answer if no retrieved-context chunks score above 0.6 similarity.\"\n- \"Bedrock Guardrails standard content filters (hate / sexual / violence). PCI: never echo back the full card PAN, even if the user types it. Jailbreak prompt-injection denial.\"\n- \"Healthcare: PHI-aware redaction on all log channels. Strict topic restriction — no diagnostic claims, no medication dosing advice, no triage decisions. Always recommend escalation to a clinician for clinical questions.\"",
        type: { kind: 'textarea', rows: 2, placeholder: 'PII redaction, content filtering, topic restrictions…' },
      },
      {
        key: 'hitl_requirements',
        label: 'Human-in-the-loop requirements',
        hint:
          'Which agent actions require human approval before execution.',
        help:
          "## What HITL means\n\n**HITL = Human-In-The-Loop.** It's the rule: \"the agent can suggest or draft this, but a human must review and approve before it actually happens.\"\n\nWithout HITL, an agent might autonomously send an email to a customer, file a SEC filing, refund a charge, or change a clinical note. **With** HITL, those actions are queued for a human to confirm.\n\n## What to capture\n\nList the categories of action that **must** have human approval at this customer:\n\n- **Customer-facing communications** — emails, chat replies, social posts.\n- **Financial transactions** — refunds, payments, fund transfers, contract pricing.\n- **Data writes / deletions** — anything that modifies customer records, clinical notes, financial books.\n- **Regulated decisions** — credit decisions, medical recommendations, hiring/firing, prior-auth submissions.\n- **External submissions** — RFP responses, SEC filings, regulatory filings.\n- **Above-threshold actions** — \"any refund > $500\", \"any contract > $10k\".\n\n## Why this drives proposal design\n\nEach HITL category becomes an explicit step in the agent's workflow: the agent drafts, queues for review, and only acts when a human clicks **Approve**. AgentCore supports this natively but the proposal has to spell out which categories need it.\n\n## How to evaluate\n\nIf the customer says \"we want full autonomy, no human review\" — push back. Regulated industries (healthcare, finance) **must** have HITL on customer-facing or financial actions; their compliance team will require it. Even unregulated industries usually want HITL on anything irreversible or above a $$ threshold.\n\nWrite down what they say, then add the categories the consultant knows from experience their compliance team will demand.",
        type: { kind: 'textarea', rows: 2 },
        priority: 'recommended',
      },
      {
        key: 'audit_retention',
        label: 'Audit / logging retention requirements',
        hint:
          'How long agent activity logs must be retained (90 days, 1 year, 7 years for finance, indefinitely for healthcare). Drives CloudWatch Logs / S3 retention policy.',
        help:
          "## Examples\n\n- \"7 years on all agent transcripts and decision logs (SOX retention). CloudWatch Logs to Kinesis Firehose → S3 with Object Lock in Glacier Deep Archive after 90 days.\"\n- \"Indefinite retention on anything touching a clinical workflow (HIPAA 6-year minimum, internal policy keeps forever). 1-year retention on non-clinical agent activity.\"\n- \"No formal retention requirement — default to 90 days hot, 1 year warm in S3 for cost-control. Revisit if compliance scope grows.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'prohibitions',
        label: 'Explicit prohibitions on AI-driven decisions',
        hint:
          'Decisions the customer explicitly prohibits AI from making autonomously — credit decisions, hiring, medical diagnoses, legal advice. Hard-coded into AgentCore Policy as never-allowed actions.',
        help:
          "## What to capture\n\nActions the customer's compliance, legal, or risk team has flagged as never-autonomous. Each one becomes a hard deny in AgentCore Policy; the agent may suggest but a human must execute.\n\n## Examples\n\n- \"No autonomous credit decisions (ECOA / fair-lending exposure). No autonomous account closures or chargeoffs. Agent may draft recommendations; human underwriter executes.\"\n- \"No autonomous clinical diagnoses, no medication dosing or substitution, no admission/discharge decisions. Agent may surface evidence and draft notes for clinician review only.\"\n- \"No autonomous hiring/firing/comp decisions. No autonomous legal advice or contract execution. No autonomous customer refunds > $500.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'maturity_score',
        label: 'Governance readiness maturity (0–4)',
        hint:
          'Your overall read of data + AI governance and regulatory posture: policies, review processes, guardrails, audit. Drives the Phase 2 readiness report.',
        type: { kind: 'radio', options: MATURITY_OPTIONS },
        priority: 'recommended',
      },
    ],
  },
  {
    key: 'organizational_readiness',
    number: 8,
    phase: 'intake',
    title: 'Organizational Readiness',
    navTitle: 'Org Readiness',
    description:
      'Whether the customer can execute: executive sponsor, budget, team and skills, change management, and timeline. Surfaces the people and process gaps a POC depends on.',
    fields: [
      {
        key: 'executive_sponsor',
        label: 'Executive sponsor (name, role)',
        hint:
          'The named executive whose budget funds the POC and who will sign the customer sign-off. Without a real sponsor, the POC won\'t survive the first internal review. "VP Customer Operations" works; "TBD" doesn\'t.',
        help:
          "## Examples\n\n- \"Jane Doe — VP Customer Operations\"\n- \"Raj Patel — CTO\"\n- \"Linda Wang — VP Sales Operations (proxy for the CRO who is the actual budget holder)\"",
        type: { kind: 'textarea', rows: 2 },
        priority: 'recommended',
      },
      {
        key: 'sponsor_commitment',
        label: 'Sponsor commitment level',
        hint:
          "Honest read of where the sponsor sits. \"Strongly supportive\" — actively pushing the project. \"Supportive with conditions\" — willing but needs specific milestones to keep going. \"Skeptical\" — flag in the assessment; the POC needs to address their concerns explicitly.",
        type: { kind: 'radio', options: SPONSOR_COMMITMENT_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'budget_status',
        label: 'Budget status',
        hint:
          'Is POC budget already allocated? "Confirmed" — money is set aside. "Under discussion" — informal alignment, no formal allocation yet. "Not yet" — POC funding is part of the conversation the assessment will drive.',
        type: { kind: 'radio', options: BUDGET_STATUS_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'budget_amount',
        label: 'Confirmed budget $',
        hint:
          'If budget is confirmed, the amount. Helps scope the POC realistically. Typical agentic POC budgets run $150K–$500K.',
        help:
          "## Examples\n\n- \"$300K confirmed for the POC, separate FY26 line for production rollout.\"\n- \"$150K of internal labor allocated, no external spend yet — partner POC fees would need a fresh approval.\"\n- \"$0 confirmed today — this assessment is the artifact that funds the conversation.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'team_composition',
        label: 'Team that would build & operate agents (size, roles)',
        hint:
          'The internal team that would actually deliver the POC after assessment ends — size, roles (engineering, product, ops). 0-person team means the customer will need AllCode\'s delivery team, which changes the POC SOW.',
        help:
          "## Examples\n\n- \"4-person AI platform team: 1 staff engineer (lead), 2 mid-level engineers, 1 ML engineer. Cloud platform team supports infra. PM allocated 25%.\"\n- \"No dedicated AI team. 2 senior engineers from the data platform team would split time (~50% each). AllCode delivery would own the agentic-runtime work end-to-end.\"\n- \"Customer has no team. AllCode would deliver fully, with a customer SME at ~10% allocation for domain Q&A.\"",
        type: { kind: 'textarea', rows: 3 },
        priority: 'recommended',
      },
      {
        key: 'team_skills',
        label: 'Team skill with AWS / Bedrock / agent frameworks / Python',
        hint:
          'Skill self-assessment of the build team. Strong AWS but new to Bedrock — POC includes enablement. Strong Python but no AWS — front-load AWS onboarding. Strong on agent frameworks (LangChain, etc.) — they can move fast.',
        help:
          "## Examples\n\n- \"Strong AWS (cert-heavy team), new to Bedrock and agents. Solid Python. POC plan needs ~1 week of Bedrock + Strands enablement.\"\n- \"Mixed: one engineer is deep on LangChain + Bedrock pilots, the other three are mostly Java backend. Plan is to pair the experienced engineer as a lead.\"\n- \"No AWS skill at all — team is Azure-native. POC plan needs to front-load AWS onboarding (~3 weeks), or AllCode owns the AWS side and the customer team takes over once production opens.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'training_certs',
        label: 'AI/ML training or certifications completed',
        hint:
          'Formal training the team has done — AWS ML Specialty, Anthropic skill series, Coursera ML, vendor certs. Often a credibility signal for the sponsor; also fills the "are we ready" question.',
        help:
          "## Examples\n\n- \"All 4 engineers have AWS Solutions Architect Associate. Lead has Anthropic agent-skills + AWS ML Specialty.\"\n- \"Two engineers completed the Bedrock + Strands self-paced workshop in 2025. No formal certs.\"\n- \"No formal AI/ML training across the team yet. Sponsor wants the POC to include enablement time.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'change_mgmt',
        label: 'Change management maturity',
        hint:
          'How well the customer rolls out new tools internally. Mature orgs have formal change management; ad-hoc orgs have "the new tool sat unused for 6 months because no one trained anyone." Affects POC adoption planning.',
        help:
          "## Examples\n\n- \"Mature. Dedicated change-management team handles cohort rollout, training, and adoption metrics. Have done 6 enterprise tool rollouts in the last 3 years.\"\n- \"Mixed. IT can ship a tool, but adoption is uneven — last CRM rollout took 18 months to hit 80% usage. Plan to over-invest in agent UX + training for this POC.\"\n- \"Weak. No change-management function. Tools have historically been launched and forgotten. Sponsor agrees the POC needs an explicit adoption plan, not just a launch event.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'timeline',
        label: 'Preferred timeline & phasing willingness',
        hint:
          'When the customer wants to be live (or have measurable results) and whether they\'re open to a phased rollout vs. demanding a big-bang launch. Phased is almost always the right answer; helps surface unrealistic expectations early.',
        help:
          "## Examples\n\n- \"Want measurable results in 90 days (POC), production in 6 months. Open to phased rollout — a small user cohort first is fine.\"\n- \"FY26 board commit is 'AI in production by Q3.' Hard date. Phased is acceptable if Q3 milestone is at least one production cohort.\"\n- \"No specific deadline. Sponsor wants quality over speed; phased is preferred.\"",
        type: { kind: 'textarea', rows: 2 },
        priority: 'recommended',
      },
      {
        key: 'stakeholders',
        label: 'Decision-chain stakeholders needing to approve',
        hint:
          'Everyone whose sign-off is needed before the POC can move forward — legal, security, procurement, IT, line-of-business leadership. Surfaces hidden gatekeepers that can stall the project.',
        help:
          "## Examples\n\n- \"VP Customer Ops (sponsor, signs SOW). CISO (security review). General Counsel (vendor agreement + AI use). VP Procurement (vendor onboarding). VP Engineering (resource allocation).\"\n- \"Sponsor + CISO + Procurement only. Legal already pre-approved the AWS DPA. No model-risk committee yet.\"\n- \"Sponsor + Chief Risk Officer + Compliance Director + IT VP. Risk Committee meets monthly — submission deadline is the 1st of each month.\"",
        type: { kind: 'textarea', rows: 2 },
      },
      {
        key: 'maturity_score',
        label: 'Organizational readiness maturity (0–4)',
        hint:
          'Your overall read of skills, change management, sponsorship, and budget commitment. Drives the Phase 2 readiness report.',
        type: { kind: 'radio', options: MATURITY_OPTIONS },
        priority: 'recommended',
      },
    ],
  },
  {
    key: 'additional_context',
    number: 9,
    phase: 'intake',
    title: 'Additional Context',
    description:
      'Anything that didn\'t fit the structured fields above. Capture questions you asked + the customer\'s answers verbatim — interview notes, sidebar conversations, follow-ups via email or Slack. Flows into the business profile as an appendix and into the AI proposal context so downstream steps see it.',
    fields: [
      {
        key: 'qa_entries',
        label: 'Free-form Q&A',
        hint:
          'Add a row per Q&A. Question on the left, answer on the right. Use this for everything that doesn\'t belong in a structured field — verbatim quotes, side comments, follow-ups, off-topic context.',
        help:
          "## When to use this section\n\nThe intake schema has fixed slots for the things AWS specifically asks about. But every customer conversation surfaces details that don't fit any of them — a passing comment about an existing vendor, an answer to a question you improvised, a constraint mentioned in a follow-up email. **Capture it here.**\n\n## What flows downstream\n\n- **Business profile**: rendered as an appendix at the bottom of `business-profile.md`. The customer sees these in the profile review.\n- **AI clarifications**: the clarify worker sees the Q&A so it doesn't re-ask things you already covered.\n- **Proposal suggestions**: the suggester reads the Q&A as customer context when proposing solutions.\n\n## Examples\n\n- **Q**: Have you considered just buying Salesforce Einstein instead of building? **A**: Yes — evaluated last quarter, ruled out because of multi-CRM data scope.\n- **Q**: Who chose the current vector store? **A**: Lead engineer's preference; no formal evaluation. Open to changing.\n- **Q**: Any hard dates from compliance? **A**: SOC 2 Type II audit window opens 2026-09-01 — POC must clear governance before then.\n\n## What to leave OUT\n\n- Things that have a structured field (use the field).\n- AI-generated clarifications + answers — those already get captured automatically by the clarify loop.",
        type: { kind: 'qa-list' },
        priority: 'recommended',
      },
    ],
  },
  {
    key: 'use_case_hypotheses',
    number: 10,
    phase: 'proposals',
    title: 'Use Case Hypotheses',
    description:
      '3 to 8 candidate use cases — the discovery list this assessment is built on. Easiest filled last: once your proposals exist, hit “Generate use cases” to derive the list from them, then add or trim by hand. If fewer than 3 are captured, the assessment rounds the list out from the Industry Use Case Catalog.',
    help: `## What this is

The list of **AI / agent use cases** this engagement considered — the discovery summary behind your proposals.

## Fill it last — derive it from your proposals

Build your proposals first (above). Then come back here and click **✨ Generate use cases** to derive this list from those proposals plus the intake — each proposal becomes a row, and the AI rounds the list out with any intake-grounded ideas you haven't written up yet. Review what it adds, then edit or add rows by hand. You can also type rows in directly without generating.

## Why it matters

1. It's the candidate list the **Use Case Discovery & Prioritization** deliverable is built from.
2. If you capture fewer than 3, the assessment rounds the list out from the Industry Use Case Catalog — so you're never staring at a blank page.
3. The customer's own ranking (if they've expressed one) drives which use case the executive readout recommends piloting first.

## What goes here

One-liners are fine — the Proposals editor above is where a use case gets fully structured (feasibility, ROI, readiness):

> - "Contact-center copilot — draft replies from order history" (New)
> - "Turn the existing FAQ chatbot into a real agent" (Modernization)
> - "Move the fraud model off Azure onto Bedrock" (Migration)`,
    fields: [
      {
        key: 'use_cases',
        label: 'Candidate use cases',
        hint:
          'The candidate use cases this engagement looked at. Derive them from your proposals with “Generate use cases”, or list them by hand. Type = New (brand-new workflow), Modernization (e.g., chatbot → agent), Migration (e.g., from Azure AI Studio).',
        type: { kind: 'use-cases-table' },
        priority: 'recommended',
      },
      {
        key: 'customer_ranking',
        label: "Customer's own ranking (if expressed)",
        hint:
          'If the customer has already prioritized the use cases verbally ("contact-center first, then forecasting"), capture it here. Drives the executive readout\'s recommended POC selection.',
        help:
          "## Examples\n\n- \"Sponsor's priority: (1) Contact-center copilot — biggest pain, (2) Inventory forecasting agent, (3) Internal Confluence Q&A. Wants the POC to start with #1.\"\n- \"Customer has not ranked yet — wants the assessment to recommend an order based on ROI + feasibility.\"\n- \"Stack-ranked: (1) Underwriter pre-screen (compliance pressure), (2) Sales-quote assistant (revenue), (3) Engineering coding assistant (internal). Order matters — board reviews progress on #1 monthly.\"",
        type: { kind: 'textarea', rows: 2 },
      },
    ],
  },
  {
    key: 'engagement_metadata',
    number: 11,
    phase: 'engagement',
    title: 'Engagement Metadata',
    description:
      "The AWS funding paperwork for this engagement: sprint window, Fund Request dates, the ACE opportunity and fund-request IDs, and the AWS account team. It doesn't describe the solution; it's the bookkeeping the submission needs.",
    help: `## What this is

The **administrative bookkeeping AWS requires** to fund this engagement — the program / sprint window, the Fund Request dates, the AWS opportunity and fund-request IDs, and the AWS account team. None of it describes the *solution*; it's the paperwork that lets the funding claim be filed.

## Why it matters

These values flow straight into the **assessment report** and the **customer sign-off**. Without them the funding claim can't be submitted — so they're worth getting right even though they aren't technical.

## Where the values come from

| Field | Who provides it |
|---|---|
| Sprint / FR end date | The AWS funding program calendar (Sprint 1 defaults are pre-filled) |
| ACE Opportunity ID | The AWS Account Manager / PDM, in AWS ACE |
| Fund Request ID | Created in APFP when the funding request is filed |
| Account team (AM / SA / EM) | The AWS + AllCode people staffed on the engagement |

If you don't have an ID yet, leave it blank and fill it in before submission.`,
    fields: [
      {
        key: 'sprint',
        label: 'Sprint',
        hint:
          'The AWS funding sprint this engagement falls into (e.g. Sprint 1 — Modernization to Agents). Each quarterly sprint sets its own delivery + claim deadlines, so confirm the active sprint\'s dates in the Partner Central "Key dates" article rather than assuming a fixed date.',
        type: { kind: 'text', placeholder: 'Sprint 1 — Modernization to Agents (FY26)' },
        priority: 'required',
      },
      {
        key: 'planned_fr_end_date',
        label: 'Planned FR end date',
        hint:
          'The Fund Request delivery end date submitted to AWS in APFP. Must be within the active sprint\'s published window (confirm in the Key dates article). The Fund Request expires 30 days after this date — the cash claim and invoice must be submitted before then.',
        type: { kind: 'text', placeholder: 'YYYY-MM-DD' },
        priority: 'recommended',
      },
      {
        key: 'ace_opportunity_id',
        label: 'ACE Opportunity ID',
        hint:
          'The opportunity ID in AWS\'s ACE (AWS Connect for Partners). Format starts with "O" followed by digits. Required at FR-submission time. Created by the AWS Account Manager / PDM.',
        type: { kind: 'text', placeholder: 'O123456' },
        priority: 'required',
      },
      {
        key: 'fund_request_id',
        label: 'Fund Request ID',
        hint:
          'The Fund Request (benefit application) ID issued by APFP (AWS Partner Funding Platform) when the partner submits the funding application. Copy it exactly from APFP — there is no fixed format (e.g. "benappl-6f6u99377qgxa5").',
        type: { kind: 'text', placeholder: 'benappl-6f6u99377qgxa5' },
        priority: 'required',
      },
      {
        key: 'funding_amount',
        label: 'Targeted funding amount $',
        hint:
          'How much partner funding the engagement is targeting. Capped at $30K (Enterprise/ISV/STRAT) or $15K (SMB/SUP/MMT/Mixed) per program rules. Paid 100% post-delivery via Amazon Payee Central.',
        type: { kind: 'text' },
        priority: 'recommended',
      },
      {
        key: 'planned_start_date',
        label: 'Planned start date (must be ≥14 days from FR submission)',
        hint:
          'Earliest date work begins. AWS requires the start to be at least 14 days after the FR is submitted to allow review time. Going earlier without an exception is non-compliant.',
        type: { kind: 'text' },
        priority: 'required',
      },
      {
        key: 'planned_end_date',
        label: 'Planned end date',
        hint:
          'Date the assessment is delivered to the customer — within the active sprint\'s published delivery window (confirm in the Key dates article). The cash claim must follow before the Fund Request expires (30 days after the planned FR end date).',
        type: { kind: 'text' },
        priority: 'required',
      },
      {
        key: 'signoff_contact_name',
        label: 'Customer sign-off — authorized signatory (full name)',
        hint:
          'The customer-side person who will sign the AWS Customer Sign-Off. Must be an authorized signatory (confirms the work was delivered). Their name is pre-filled as "Printed Name" on the sign-off PDF.',
        help: `## Who this is

The **customer** employee with authority to sign off that the assessment was delivered — the person whose name + title go on AWS's Customer Sign-Off form and who actually signs it (wet signature, DocuSign, or Adobe Fill & Sign).

This is usually the executive sponsor or their delegate, and is typically one of the people you already listed under **Decision-chain stakeholders needing to approve**. It must be someone authorized to sign on the customer's behalf — AWS rejects the claim otherwise.

## Examples

- "Jane Doe"
- "Dr. Raj Patel"
- "Lin Wang"

It flows verbatim onto the sign-off PDF as **Printed Name**.`,
        type: { kind: 'text', placeholder: 'Jane Doe' },
        priority: 'required',
      },
      {
        key: 'signoff_contact_title',
        label: 'Customer sign-off — signatory title',
        hint:
          "The signatory's job title at the customer. Pre-filled as \"Position Title\" on the sign-off PDF — establishes they're authorized to sign.",
        type: { kind: 'text', placeholder: 'VP Customer Operations' },
        priority: 'required',
      },
      {
        key: 'signoff_contact_email',
        label: 'Customer sign-off — signatory email',
        hint:
          'Email of the authorized signatory — used to route the sign-off for signature and as the contact of record. Not printed on the AWS form, but required to complete the sign-off.',
        type: { kind: 'text', placeholder: 'jane.doe@customer.com' },
        priority: 'required',
      },
      {
        key: 'engagement_manager',
        label: 'Partner engagement manager',
        hint:
          'AllCode\'s engagement manager — the named partner-side contact who owns delivery for this assessment. Defaults to Joel Garcia.',
        type: { kind: 'text', placeholder: 'Joel Garcia' },
        priority: 'required',
      },
      {
        key: 'pdm',
        label: 'Partner Development Manager (PDM)',
        hint:
          'AllCode\'s AWS Partner Development Manager — an org-level relationship (not specific to this customer). Defaults to Abby Glova.',
        type: { kind: 'text', placeholder: 'Abby Glova' },
      },
      {
        key: 'solution_architect',
        label: 'Partner solution architect',
        hint:
          'AllCode\'s solution architect — owns the technical recommendations and Phase 3 deliverable. Different from the AWS-side AgentCore SA.',
        type: { kind: 'text' },
      },
      {
        key: 'aws_account_manager',
        label: 'AWS Account Manager / PDM (named)',
        hint:
          'The AWS Account Manager (for end customers) or PDM (Partner Development Manager, for partner accounts) on the AWS side. They create the ACE Opportunity and validate segmentation.',
        type: { kind: 'text' },
        priority: 'required',
      },
      {
        key: 'agentcore_sa',
        label: 'AgentCore SA (assigned)',
        hint:
          'The Amazon Bedrock AgentCore Solution Architect AWS assigns to support the engagement. SME pool for Sprint 1: Veena Vasudevan.',
        type: { kind: 'text', placeholder: 'SME pool: Veena Vasudevan' },
      },
      {
        key: 'quick_sa',
        label: 'Quick Suite SA (assigned)',
        hint:
          'The Amazon Quick Suite Specialist AWS assigns. Required if any proposed solution uses Quick Suite. SME pool for Sprint 1: Ricardo Arriaga Rico.',
        type: { kind: 'text', placeholder: 'SME pool: Ricardo Arriaga Rico' },
      },
      {
        key: 'data_sa',
        label: 'Data Foundations SA',
        hint:
          'The Data Foundations for AI Specialist AWS may assign for engagements with significant data-readiness gaps. SME pool: Jessica Enzi.',
        type: { kind: 'text', placeholder: 'SME pool: Jessica Enzi' },
      },
    ],
  },
]

export function findSection(key: string): SectionDef | undefined {
  return SECTIONS.find((s) => s.key === key)
}
